/**
 * Loading the save under another device's code: a permanent replacement of what is on this
 * device, so it asks first (08 §5.3) and says what happens to the local saves. When it is done
 * the game has to start again - the profile is read once, at start-up - so the way out is the
 * title by way of `port.restart`.
 */

import { formatCode } from '../../../platform/cloudSave/saveCode';
import type { Ctx } from '../context';
import { errorMessage } from '../dom';
import { confirm, notice } from '../widgets/confirm';
import type { CloudPort } from './cloudPort';

export interface LoadHooks {
  /** The request is in flight: the screen locks its controls and its way back. */
  setBusy(on: boolean): void;
  /** It was not done; `why` is for the player. */
  refuse(why: string): void;
}

/** `code` has already passed the checksum. */
export function loadFromCode(ctx: Ctx, port: CloudPort, code: string, hooks: LoadHooks): void {
  confirm(ctx, {
    title: 'REPLACE THIS DEVICE\'S SAVES?',
    lines: [
      `THE SAVE STORED UNDER ${formatCode(code)} WILL REPLACE EVERYTHING ON THIS DEVICE: THE COMPANY, THE HALL OF`
        + ' HONOR, THE RECORDS, THE SETTINGS AND THE KEYS.',
      'WHAT IS ON THIS DEVICE NOW IS NOT DESTROYED: IT IS KEPT IN THIS BROWSER AS A BACKUP, TOGETHER WITH THE CODE'
        + ' IT WAS USING. IT IS NO LONGER THE GAME YOU PLAY HERE.',
      'FROM THEN ON BOTH DEVICES SHARE ONE SAVE.',
    ],
    confirmLabel: 'REPLACE',
    danger: true,
    onConfirm: () => {
      hooks.setBusy(true);
      let asked: Promise<string | null>;
      try {
        asked = port.useCode(code);
      } catch (err) {
        asked = Promise.reject(err);
      }
      asked.then(
        (problem) => {
          hooks.setBusy(false);
          if (problem !== null) {
            hooks.refuse(problem);
            return;
          }
          notice(
            ctx,
            'SAVE LOADED',
            ['THE SAVE UNDER THAT CODE IS NOW ON THIS DEVICE. THE GAME RETURNS TO THE TITLE TO READ IT.'],
            () => {
              port.restart?.();
              ctx.nav.title();
            },
          );
        },
        (err: unknown) => {
          hooks.setBusy(false);
          hooks.refuse(errorMessage(err));
        },
      );
    },
  });
}
